import type React from "react";
import {
  type FC,
  type MutableRefObject,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Cell,
  Customized,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { getAlpha } from "senf-shared/utils/helpers";
import { v4 as uuidv4 } from "uuid";
import { cn } from "../../../util/classNameConstructor";
import { ChartTooltip } from "../../core/charts/ChartTooltip";
import {
  Legend as CustomizedLegend,
  type LegendPayload,
} from "../../core/charts/Legend";
import type { ChartsProps } from "./Charts.types";

const RADIAN = Math.PI / 180;

const getCosSin = (midAngle: number): { sin: number; cos: number } => {
  const angleRadians = -RADIAN * midAngle;
  return { sin: Math.sin(angleRadians), cos: Math.cos(angleRadians) };
};

export type Position = {
  cx: number;
  cy: number;
  sx: number;
  sy: number;
  mx: number;
  my: number;
  d: number;
  sin: number;
  cos: number;
  midAngle: number;
  distance: number;
  right: boolean;
  bound: number;
};

interface PieSectorProps {
  midAngle: number;
  outerRadius: number;
  cx: number;
  cy: number;
  valueLabel?: string;
  label?: string;
  index?: number;
  // Additional properties may be passed from recharts.
  [key: string]: any;
}

interface PieLabelRenderProps extends PieSectorProps {
  stroke?: string;
  fill?: string;
  valueLabel?: string;
  label?: string;
  index: number;
  payload: {
    payload: {
      stroke?: string;
      [key: string]: any;
    };
    [key: string]: any;
  };
  hideLabel?: boolean;
  key: string | number;
}

interface Margin {
  top: number;
  bottom: number;
}

/**
 * Calculates label and connector positions for a given pie slice.
 *
 * @param props - Properties including midAngle, outerRadius, cx, and cy.
 * @param defaultDistance - Base distance used to calculate the positions.
 * @returns Position object with computed coordinates.
 *
 * /* PERF: Consider memoizing results if the same props are used repeatedly. */
const calculatePositions = (
  { midAngle, outerRadius, cx, cy, ...rest }: PieSectorProps,
  defaultDistance: number,
): Position => {
  const { sin, cos } = getCosSin(midAngle);
  const sx = cx + outerRadius * cos;
  const sy = cy + outerRadius * sin;
  const distance = Math.abs(sin) > 0.98 ? defaultDistance + 5 : defaultDistance;
  const mx = cx + (outerRadius + distance) * cos;
  const my = cy + (outerRadius + distance) * sin;
  const d = sin !== 0 ? -distance / sin : 0;
  const right = cos >= 0;
  const sign = cy - my > 0 ? 1 : -1;
  const bound = Math.max(Math.abs(cy - my + 20 * sign) - outerRadius, 0) * sign;
  return {
    cx,
    cy,
    sx,
    sy,
    mx,
    my,
    d,
    cos,
    sin,
    midAngle,
    distance,
    right,
    bound,
  };
};

type LabelProps = {
  positions: Position;
  color: string;
  hideLabel: boolean;
  label: string;
  value: string;
  offsetDistance: number;
};

const Label: FC<LabelProps> = ({
  positions,
  color = "var(--surface-depressed)",
  hideLabel,
  label,
  value,
  offsetDistance,
}): ReactElement | null => {
  const { sx, sy, mx, my, right } = positions;
  const ex = mx + (right ? 1 : -1) * offsetDistance;
  const textAnchor = right ? "start" : "end";
  if ([sx, sy, mx, my, ex].some((e) => Number.isNaN(e))) return null;
  return (
    <g>
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${my}`}
        stroke={color}
        fill="none"
      />
      <circle cx={ex} cy={my} r={2} fill={color} stroke="none" />
      <text
        x={ex + (right ? 1 : -1) * 12}
        y={my + (hideLabel ? 5 : 0)}
        textAnchor={textAnchor}
        className="body-lg-bold fill-text-default"
      >
        {label}
      </text>
      {!hideLabel && (
        <text
          x={ex + (right ? 1 : -1) * 12}
          y={my}
          dy={15}
          textAnchor={textAnchor}
          className="body-sm fill-text-subdued"
        >
          {value}
        </text>
      )}
    </g>
  );
};

const CenterLabel = ({
  center,
  margin,
  width,
  height,
}: {
  margin: Margin;
  center?: { label?: string; value: string };
  width: number;
  height: number;
}): ReactElement | null => {
  if (!center) return null;
  return (
    <g>
      <text
        x={width / 2}
        y={height / 2}
        dy={margin.top - (center.label ? 50 : 37)}
        className="text-3xl font-bold fill-text-default"
        textAnchor="middle"
      >
        {center.value}
      </text>
      {center.label && (
        <text
          x={width / 2}
          y={height / 2}
          dy={margin.top - 25}
          className="body-md fill-text-subdued"
          textAnchor="middle"
        >
          {center.label}
        </text>
      )}
    </g>
  );
};

interface PieChartProps extends ChartsProps {
  innerRadius?: number;
  outerRadius?: number;
  label?: boolean;
  legendReference?: boolean;
  minHeight?: number;
  width?: number;
  isAnimationActive?: boolean;
  legend?: LegendPayload[];
  pathLength?: { r?: number; x?: number };
  annotation?: string;
  center?: {
    label: string;
    value: string;
  };
}

interface ExtendedLabelProps {
  data: ChartsProps["data"];
  firstItem: ChartsProps["data"][number];
  getKey: (key: string) => string;
  legendReference: boolean | undefined;
  showLegend: boolean;
  positionsMap: MutableRefObject<{ [key: string]: Position }>;
  sum: number;
  triggerRecomputeMargins: React.Dispatch<React.SetStateAction<number>>;
  recomputeMargins: number;
  pathLength?: { r?: number; x?: number };
}

const isLeft = (pos: Position): boolean | undefined => {
  if (!pos) return undefined;
  return pos.cos < 0;
};
const isTop = (pos: Position): boolean | undefined => {
  if (!pos) return undefined;
  return pos.sin < 0;
};

export const ExtendedLabel = ({
  props,
  ...sector
}: PieLabelRenderProps & {
  props: ExtendedLabelProps;
}): ReactElement | null => {
  const {
    stroke,
    fill,
    valueLabel,
    label,
    payload,
    index,
    midAngle,
    hideLabel = false,
  } = sector;
  const {
    data,
    firstItem,
    getKey,
    legendReference,
    pathLength,
    positionsMap,
    showLegend,
    sum,
    triggerRecomputeMargins,
    recomputeMargins,
  } = props;

  useEffect(() => {
    if (sector.index === data.length - 1 && recomputeMargins < 3) {
      triggerRecomputeMargins((e) => e + 1);
    }
  }, [sector.index, data.length, recomputeMargins, triggerRecomputeMargins]);

  const key: string = getKey(String(sector.key || sector.payload.key));

  const prevItem: ChartsProps["data"][number] = data[index + 1] || firstItem;
  const nextItem: ChartsProps["data"][number] | undefined = data[index - 1];
  const firstItemKey: string = getKey(firstItem?.key);
  const prevItemKey: string = getKey(prevItem?.key);
  const nextItemKey: string = nextItem ? getKey(nextItem?.key) : "";

  const { sin, cos } = getCosSin(midAngle);
  const radiusDistance = pathLength?.r || 20;
  const offsetDistance = pathLength?.x || 20;

  // Initial calculation of positions.
  const bounds = {
    [key]: Math.abs(positionsMap.current[key]?.bound),
    [prevItemKey]: Math.abs(positionsMap.current[prevItemKey]?.bound),
    [nextItemKey]: Math.abs(positionsMap.current[nextItemKey]?.bound),
  };

  const verticalDistance =
    isLeft(positionsMap.current[key]) &&
    (bounds[key] > bounds[prevItemKey]
      ? 50 * sin - 40 * Math.abs(cos)
      : 30 * sin - 10 * Math.abs(cos));

  const size = (360 / sum) * sector.value;

  positionsMap.current[key] = calculatePositions(
    sector,
    Math.max(radiusDistance, size < 45 ? verticalDistance : 0),
  );

  const inFourthQuadrant = (posKey: string): boolean => {
    const pos = positionsMap.current[posKey];
    return isLeft(pos) && isTop(pos);
  };
  if (nextItemKey && inFourthQuadrant(key)) {
    const verticalDistanceLeft: number =
      radiusDistance +
      50 -
      (positionsMap.current[nextItemKey]?.my - positionsMap.current[key].my);
    const lastQuadrantPositions: Position = calculatePositions(
      sector,
      Math.max(radiusDistance, verticalDistanceLeft),
    );
    if (
      lastQuadrantPositions.bound > radiusDistance + 25 &&
      positionsMap.current[prevItemKey]?.bound <
        positionsMap.current[nextItemKey]?.bound
    ) {
      const addition: number = positionsMap.current[firstItemKey]?.bound || 0;
      const verticalDistanceRight: number = Math.max(
        radiusDistance,
        radiusDistance +
          50 *
            (data.length - index - 1) *
            (0.8 / Math.abs(lastQuadrantPositions.sin)) +
          addition,
      );
      const positions: Position = calculatePositions(
        sector,
        verticalDistanceRight,
      );
      positionsMap.current[key] = { ...positions, right: true };
    } else {
      positionsMap.current[key] = lastQuadrantPositions;
    }
  }

  return (
    <Label
      {...sector}
      positions={positionsMap.current[key]}
      offsetDistance={offsetDistance}
      color={
        payload.payload.stroke || stroke || fill || "var(--palette-neutral-300)"
      }
      label={valueLabel || label || sector.value}
      value={
        showLegend && legendReference
          ? getAlpha(sector.initialOrder ?? index)
          : label || ""
      }
      hideLabel={hideLabel}
    />
  );
};

const PieChart: FC<PieChartProps> = ({
  data: d,
  label: showLabels,
  legend,
  legendReference,
  annotation,
  center,
  isAnimationActive = true,
  pathLength,
  minHeight = 360,
  width = 500,
  innerRadius = 50,
  outerRadius = 80,
}): ReactElement => {
  /* PERF: Consider reducing re-renders by reviewing the need for recomputeMargins state updates */
  const [recomputeMargins, triggerRecomputeMargins] = useState<number>(0);
  const uid = useRef<string>(uuidv4());

  // Use a ref to store positions for each slice; prevents re-creation on every render.
  const positionsMap = useRef<{ [key: string]: Position }>({});

  // Ensure data exists; if not, provide a fallback.
  const data: ChartsProps["data"][number][] = useMemo(() => {
    if (!d.length) {
      return [
        {
          name: "No data",
          value: 1,
          fill: "var(--palette-neutral-300)",
          key: "no-data",
        },
      ];
    }
    return d.filter((e) => !!e.value);
  }, [d]);

  const sum: number = useMemo(
    () =>
      data.reduce(
        (acc: number, curr: ChartsProps["data"][number]) => acc + curr.value,
        0,
      ),
    [data],
  );
  const cornerRadius = (value: number): number =>
    Math.min(5, Math.round((value / sum) * 100));

  // Compute the largest label length.
  const largestCharLength: number = useMemo(
    () =>
      data.reduce((max: number, item: ChartsProps["data"][number]) => {
        return Math.max(max, String(item.label).length);
      }, 0),
    [data],
  );
  const breakpoint: number = largestCharLength * 30;
  const showLegend: boolean = useMemo(
    () =>
      data.some((item) => String(item.label).length > 50) || width < breakpoint,
    [data, width, breakpoint],
  );

  const legendPayload: LegendPayload[] | undefined = useMemo(
    () =>
      legend?.map((item, index) => ({
        ...item,
        legendReference:
          showLegend && legendReference
            ? getAlpha(item.initialOrder ?? index)
            : undefined,
      })),
    [legend, legendReference, showLegend],
  );

  const firstItem: ChartsProps["data"][number] = data[0];

  // Stable key generator using a unique id.
  const getKey = useCallback(
    (key: string): string => `${uid.current}-${key}`,
    [],
  );

  // Compute margins based on the label positions.
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const margin: Margin = useMemo((): Margin => {
    const offsets: number[] = data
      .map(({ key }) => positionsMap.current[getKey(key)]?.bound || 0)
      .filter((e) => !!e);
    const defaultMargin: Margin = { top: 50, bottom: 50 };
    if (!offsets.length) return defaultMargin;

    return {
      top: Math.max(Math.max(0, ...offsets) + 30, 50),
      bottom: Math.max(Math.abs(Math.min(0, ...offsets)) + 30, 50),
    };
  }, [data, recomputeMargins]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const labelPayload: ExtendedLabelProps = useMemo(() => {
    return {
      pathLength,
      data,
      firstItem,
      legendReference,
      showLegend,
      positionsMap,
      sum,
      getKey,
      triggerRecomputeMargins,
      recomputeMargins,
    };
  }, [
    pathLength,
    data,
    firstItem,
    legendReference,
    showLegend,
    sum,
    recomputeMargins,
  ]);

  return (
    <div
      className={cn(
        "h-full w-full duration-200",
        recomputeMargins ? "opacity-100" : "opacity-0",
      )}
    >
      <ResponsiveContainer
        minHeight={Math.max(
          minHeight,
          outerRadius * 2 + (margin.top + margin.bottom),
        )}
      >
        <RechartsPieChart margin={margin}>
          <Customized
            component={<CenterLabel center={center} margin={margin} />}
          />
          <Pie
            dataKey="value"
            data={data}
            startAngle={90}
            endAngle={-270}
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            label={
              showLabels &&
              (({ key, ...props }) => (
                <ExtendedLabel props={labelPayload} key={key} {...props} />
              ))
            }
            labelLine={false}
            isAnimationActive={isAnimationActive}
            paddingAngle={3}
            cornerRadius={5}
          >
            {data.map((entry: ChartsProps["data"][number]) => (
              <Cell
                key={`cell-${entry.key}`}
                fill={entry.fill}
                stroke="transparent"
                // @ts-ignore - Recharts supports cornerRadius, but TS types may not be updated.
                cornerRadius={cornerRadius(entry.value)}
              />
            ))}
          </Pie>
          <Tooltip
            content={<ChartTooltip />}
            payload={data}
            allowEscapeViewBox={{ y: true }}
            cursor={{ fill: "var(--action-secondary-default)" }}
            wrapperStyle={{ zIndex: 999 }}
          />
        </RechartsPieChart>
      </ResponsiveContainer>
      {(((showLegend || legend) && legendPayload) || annotation) && (
        <div className="p-5 flex flex-col gap-5">
          {(showLegend || legend) && legendPayload && (
            <CustomizedLegend payload={legendPayload} />
          )}
          {annotation && (
            <div className="text-center text-xs text-text-subdued">
              {annotation}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PieChart;
